import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, DownloadIcon, SettingsIcon, TrendingUpIcon, AlertCircleIcon, CheckCircle2Icon, InfoIcon, FileTextIcon, XIcon } from 'lucide-react';
import { BRAND } from '../../../shared/config/brand';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { getFacilityById, generateRepaymentLedger, mockFacilities } from '../mocks/financingData';
type Tab = 'overview' | 'repayment' | 'details' | 'settings';
type PayEarlyStep = 'method' | 'amount' | 'impact' | 'confirm' | 'success';
type PayEarlyMethod = 'partial' | 'increase_holdback' | 'full';
const formatMYR = (amount: number) => formatCurrencyAmount(amount, {
  currency: 'MYR'
});
export function FacilityDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  // Pay Early flow state
  const [showPayEarly, setShowPayEarly] = useState(false);
  const [payEarlyStep, setPayEarlyStep] = useState<PayEarlyStep>('method');
  const [payEarlyMethod, setPayEarlyMethod] = useState<PayEarlyMethod>('partial');
  const [payEarlyAmount, setPayEarlyAmount] = useState<number>(0);
  // Settings state
  const [holdbackRate, setHoldbackRate] = useState<number>(10);
  const [showSettingsConfirm, setShowSettingsConfirm] = useState(false);
  const [pendingHoldbackRate, setPendingHoldbackRate] = useState<number>(10);
  const facility = getFacilityById(id || '') || mockFacilities[0];
  const ledger = useMemo(() => generateRepaymentLedger(facility), [facility]);
  const handlePayEarlyClose = () => {
    setShowPayEarly(false);
    setPayEarlyStep('method');
    setPayEarlyAmount(0);
  };
  const newOutstanding = Math.max(facility.outstanding - payEarlyAmount, 0);
  const tabs: {
    key: Tab;
    label: string;
  }[] = [{
    key: 'overview',
    label: 'Overview'
  }, {
    key: 'repayment',
    label: 'Repayment ledger'
  }, {
    key: 'details',
    label: 'Details'
  }, {
    key: 'settings',
    label: 'Settings'
  }];
  return <div className="max-w-5xl mx-auto pb-12" data-id="element-4421">
      <button onClick={() => navigate('/bills/financing')} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-6" data-id="element-4422">
        <ArrowLeftIcon size={16} data-id="element-4423" />
        Back to Financing
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 mb-8" data-id="element-4424">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6" data-id="element-4425">
          <div data-id="element-4426">
            <div className="flex items-center gap-3 mb-2" data-id="element-4427">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-4428">
                {facility.id}
              </h1>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-green-700 bg-green-50 font-medium text-sm" data-id="element-4429">
                {facility.status}
              </span>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm" data-id="element-4430">
              <div data-id="element-4431">
                <span className="text-gray-500" data-id="element-4432">Outstanding balance:</span>{' '}
                <span className="font-semibold text-gray-900" data-id="element-4433">
                  {formatMYR(facility.outstanding)}
                </span>
              </div>
              <div data-id="element-4434">
                <span className="text-gray-500" data-id="element-4435">Projected close:</span>{' '}
                <span className="font-semibold text-gray-900" data-id="element-4436">
                  {facility.projectedCloseDate}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-4437">
            <button onClick={() => setShowPayEarly(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--brand-primary)] text-white text-sm font-medium hover:bg-[var(--brand-primary-hover)] transition-colors" data-id="element-4438">
              <TrendingUpIcon size={16} data-id="element-4439" />
              Pay early
            </button>
            <button onClick={() => setActiveTab('settings')} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-4440">
              <SettingsIcon size={16} data-id="element-4441" />
              Settings
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8" data-id="element-4442">
        <nav className="flex gap-8" data-id="element-4443">
          {tabs.map(tab => <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key ? 'border-[var(--brand-primary)] text-[var(--brand-primary)]' : 'border-transparent text-gray-500 hover:text-gray-900'}`} data-id="element-4444">
              {tab.label}
            </button>)}
        </nav>
      </div>

      {/* Tab content */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8" data-id="element-4445">
        {activeTab === 'overview' && <div className="space-y-8" data-id="element-4446">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-4447">
              <div className="md:col-span-2" data-id="element-4448">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-4449">
                  Repayment progress
                </h3>
                <div className="flex justify-between items-end mb-2" data-id="element-4450">
                  <span className="text-3xl font-bold text-gray-900" data-id="element-4451">
                    {formatMYR(facility.totalRepayment - facility.outstanding)}
                    <span className="text-lg text-gray-400 font-normal" data-id="element-4452">
                      {' '}
                      / {formatMYR(facility.totalRepayment)}
                    </span>
                  </span>
                  <div className="text-sm font-medium text-gray-600" data-id="element-4453">
                    {facility.repaidPercent}% repaid
                  </div>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-4" data-id="element-4454">
                  <div className="h-full bg-[var(--brand-primary)]" style={{
                width: `${facility.repaidPercent}%`
              }} data-id="element-4455" />
                </div>
                <div className="flex items-center gap-2 text-sm" data-id="element-4456">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-green-700 bg-green-50 font-medium" data-id="element-4457">
                    <CheckCircle2Icon size={14} data-id="element-4458" />
                    On track
                  </span>
                  <span className="text-gray-500" data-id="element-4459">
                    Projected to close {facility.projectedCloseDate}
                  </span>
                </div>
              </div>

              <div className="space-y-6" data-id="element-4460">
                <div data-id="element-4461">
                  <div className="text-sm text-gray-500 mb-1" data-id="element-4462">
                    Holdback rate
                  </div>
                  <div className="text-xl font-bold text-gray-900" data-id="element-4463">
                    {facility.holdbackRate}%
                  </div>
                </div>
                <div data-id="element-4464">
                  <div className="text-sm text-gray-500 mb-1" data-id="element-4465">
                    Avg. daily deduction
                  </div>
                  <div className="text-xl font-bold text-gray-900" data-id="element-4466">
                    ~{formatMYR(facility.avgDailyDeduction)} / day
                  </div>
                </div>
              </div>
            </div>
          </div>}

        {activeTab === 'repayment' && <div className="space-y-6" data-id="element-4467">
            <div className="bg-blue-50 p-4 rounded-lg flex items-start gap-3 border border-blue-100" data-id="element-4468">
              <InfoIcon size={18} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-4469" />
              <div className="text-sm text-blue-800" data-id="element-4470">
                <p className="font-medium mb-1" data-id="element-4471">Repayment is automatic</p>
                <p data-id="element-4472">
                  A {facility.holdbackRate}% holdback is applied to your daily
                  settlement volume and credited toward this facility
                  automatically.
                </p>
              </div>
            </div>

            <div className="flex justify-between items-center" data-id="element-4473">
              <h3 className="text-lg font-bold text-gray-900" data-id="element-4474">
                Repayment ledger
              </h3>
              <div className="text-sm text-gray-500" data-id="element-4475">
                Next deduction:{' '}
                <strong className="text-gray-900" data-id="element-4476">Tomorrow</strong> (~
                {formatMYR(facility.avgDailyDeduction)})
              </div>
            </div>

            <div className="overflow-x-auto" data-id="element-4477">
              <table className="w-full text-left border-collapse" data-id="element-4478">
                <thead data-id="element-4479">
                  <tr className="bg-gray-50 border-y border-gray-200 text-xs uppercase tracking-wider text-gray-500" data-id="element-4480">
                    <th className="px-4 py-3 font-medium" data-id="element-4481">Date</th>
                    <th className="px-4 py-3 font-medium" data-id="element-4482">Batch</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-4483">GMV</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-4484">
                      Deducted ({facility.holdbackRate}%)
                    </th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-4485">
                      Balance
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-4486">
                  {ledger.map(row => <tr key={row.id} className="hover:bg-gray-50" data-id="element-4487">
                      <td className="px-4 py-3 text-sm text-gray-900" data-id="element-4488">
                        {new Date(row.date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 font-mono" data-id="element-4489">
                        {row.batchRef}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-right" data-id="element-4490">
                        {formatMYR(row.gmv)}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right" data-id="element-4491">
                        {formatMYR(row.deducted)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-right" data-id="element-4492">
                        {formatMYR(row.runningBalance)}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}

        {activeTab === 'details' && <div className="space-y-8" data-id="element-4493">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-4494">
              Facility details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12" data-id="element-4495">
              <div data-id="element-4496">
                <div className="text-sm text-gray-500 mb-1" data-id="element-4497">Principal</div>
                <div className="font-medium text-gray-900" data-id="element-4498">
                  {formatMYR(facility.principal)}
                </div>
              </div>
              <div data-id="element-4499">
                <div className="text-sm text-gray-500 mb-1" data-id="element-4500">Pricing model</div>
                <div className="font-medium text-gray-900" data-id="element-4501">
                  {facility.pricingModel}
                </div>
              </div>
              <div data-id="element-4502">
                <div className="text-sm text-gray-500 mb-1" data-id="element-4503">
                  Total repayment
                </div>
                <div className="font-medium text-gray-900" data-id="element-4504">
                  {formatMYR(facility.totalRepayment)}
                </div>
              </div>
              <div data-id="element-4505">
                <div className="text-sm text-gray-500 mb-1" data-id="element-4506">Holdback rate</div>
                <div className="font-medium text-gray-900" data-id="element-4507">
                  {facility.holdbackRate}% daily
                </div>
              </div>
              <div data-id="element-4508">
                <div className="text-sm text-gray-500 mb-1" data-id="element-4509">Start date</div>
                <div className="font-medium text-gray-900" data-id="element-4510">
                  {facility.startDate}
                </div>
              </div>
              <div data-id="element-4511">
                <div className="text-sm text-gray-500 mb-1" data-id="element-4512">
                  Disbursement account
                </div>
                <div className="font-medium text-gray-900" data-id="element-4513">
                  {facility.disbursementAccount}
                </div>
              </div>
              <div data-id="element-4514">
                <div className="text-sm text-gray-500 mb-1" data-id="element-4515">
                  Target repayment window
                </div>
                <div className="font-medium text-gray-900" data-id="element-4516">
                  {facility.targetRepaymentWindow}
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200" data-id="element-4517">
              <h4 className="text-sm font-bold text-gray-900 mb-4" data-id="element-4518">
                Documents
              </h4>
              <div className="space-y-3" data-id="element-4519">
                <button className="w-full flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors" data-id="element-4520">
                  <div className="flex items-center gap-3" data-id="element-4521">
                    <FileTextIcon size={18} className="text-gray-400" data-id="element-4522" />
                    <span className="font-medium text-gray-900" data-id="element-4523">
                      Financing agreement
                    </span>
                  </div>
                  <DownloadIcon size={18} className="text-gray-500" data-id="element-4524" />
                </button>
                <button className="w-full flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors" data-id="element-4525">
                  <div className="flex items-center gap-3" data-id="element-4526">
                    <FileTextIcon size={18} className="text-gray-400" data-id="element-4527" />
                    <span className="font-medium text-gray-900" data-id="element-4528">
                      Monthly statements
                    </span>
                  </div>
                  <DownloadIcon size={18} className="text-gray-500" data-id="element-4529" />
                </button>
              </div>
            </div>
          </div>}

        {activeTab === 'settings' && <div className="space-y-8 max-w-2xl" data-id="element-4530">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-4531">
              Facility settings
            </h3>

            <div className="space-y-4" data-id="element-4532">
              <h4 className="text-sm font-bold text-gray-900" data-id="element-4533">
                Disbursement account
              </h4>
              <div className="p-4 rounded-lg border border-gray-200 bg-gray-50" data-id="element-4534">
                <div className="text-sm text-gray-500 mb-1" data-id="element-4535">Settles from</div>
                <div className="font-medium text-gray-900" data-id="element-4536">
                  {facility.disbursementAccount}
                </div>
                <p className="text-xs text-gray-500 mt-2" data-id="element-4537">
                  To change your disbursement account, please contact{' '}
                  {BRAND.name} support.
                </p>
              </div>
            </div>

            <div className="space-y-4" data-id="element-4538">
              <h4 className="text-sm font-bold text-gray-900" data-id="element-4539">Holdback rate</h4>
              <p className="text-sm text-gray-600" data-id="element-4540">
                Increasing your holdback rate repays the facility faster but
                deducts more from each day's settlement.
              </p>
              <div className="flex items-center gap-4" data-id="element-4541">
                <select value={holdbackRate} onChange={e => {
              setPendingHoldbackRate(Number(e.target.value));
              setShowSettingsConfirm(true);
            }} className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-900" data-id="element-4542">
                  <option value={10} data-id="element-4543">10% (Contracted)</option>
                  <option value={15} data-id="element-4544">15% (Accelerated)</option>
                  <option value={20} data-id="element-4545">20% (Aggressive)</option>
                </select>
              </div>

              {showSettingsConfirm && <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mt-4" data-id="element-4546">
                  <h5 className="text-sm font-bold text-amber-900 mb-2 flex items-center gap-2" data-id="element-4547">
                    <AlertCircleIcon size={16} data-id="element-4548" />
                    Increase holdback rate?
                  </h5>
                  <p className="text-sm text-amber-800 mb-4" data-id="element-4549">
                    This will change your daily holdback to{' '}
                    {pendingHoldbackRate}%, taking effect on your next
                    settlement.
                  </p>
                  <div className="flex gap-3" data-id="element-4550">
                    <button onClick={() => setShowSettingsConfirm(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-4551">
                      Cancel
                    </button>
                    <button onClick={() => {
                setHoldbackRate(pendingHoldbackRate);
                setShowSettingsConfirm(false);
              }} className="px-4 py-2 bg-[var(--brand-primary)] text-white rounded-lg text-sm font-medium hover:bg-[var(--brand-primary-hover)] transition-colors" data-id="element-4552">
                      Confirm
                    </button>
                  </div>
                </div>}
            </div>
          </div>}
      </div>

      {/* Pay Early modal */}
      <AnimatePresence data-id="element-4553">
        {showPayEarly && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-id="element-4554">
            <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.96
        }} className="bg-white rounded-xl shadow-xl w-full max-w-lg" data-id="element-4555">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center" data-id="element-4556">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-4557">Pay early</h2>
                <button onClick={handlePayEarlyClose} className="text-gray-400 hover:text-gray-600" data-id="element-4558">
                  <XIcon size={24} data-id="element-4559" />
                </button>
              </div>

              <div className="p-6" data-id="element-4560">
                {payEarlyStep === 'method' && <div className="space-y-6" data-id="element-4561">
                    <p className="text-gray-600" data-id="element-4562">
                      Choose how you'd like to pay down this facility ahead of
                      schedule.
                    </p>
                    <div className="space-y-3" data-id="element-4563">
                      {([{
                  value: 'partial' as PayEarlyMethod,
                  title: 'One-time partial payment',
                  desc: 'Pay a lump sum toward your outstanding balance.'
                }, {
                  value: 'increase_holdback' as PayEarlyMethod,
                  title: 'Increase holdback rate',
                  desc: 'Deduct more from daily settlements to repay faster.'
                }, {
                  value: 'full' as PayEarlyMethod,
                  title: 'Settle in full',
                  desc: 'Pay off the entire outstanding balance now.'
                }] as const).map(option => <label key={option.value} className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${payEarlyMethod === option.value ? 'border-[var(--brand-primary)] bg-[var(--brand-primary-light)]' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-4564">
                          <input type="radio" name="pay-early-method" checked={payEarlyMethod === option.value} onChange={() => setPayEarlyMethod(option.value)} className="mt-1" data-id="element-4565" />
                          <div data-id="element-4566">
                            <div className="font-medium text-gray-900" data-id="element-4567">
                              {option.title}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-4568">
                              {option.desc}
                            </div>
                          </div>
                        </label>)}
                    </div>
                    <button onClick={() => {
                if (payEarlyMethod === 'full') {
                  setPayEarlyAmount(facility.outstanding);
                }
                setPayEarlyStep('amount');
              }} className="w-full py-3 bg-[var(--brand-primary)] text-white rounded-lg font-medium hover:bg-[var(--brand-primary-hover)] transition-colors" data-id="element-4569">
                      Continue
                    </button>
                  </div>}

                {payEarlyStep === 'amount' && <div className="space-y-6" data-id="element-4570">
                    <div data-id="element-4571">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4572">
                        Amount to repay
                      </label>
                      <div className="relative" data-id="element-4573">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" data-id="element-4574">
                          MYR
                        </span>
                        <input type="number" value={payEarlyAmount || ''} onChange={e => setPayEarlyAmount(Number(e.target.value))} disabled={payEarlyMethod === 'full'} className="w-full pl-14 pr-4 py-3 rounded-lg border border-gray-300 text-gray-900 disabled:bg-gray-50" data-id="element-4575" />
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-4576">
                      <div className="text-sm font-medium text-gray-900 mb-2" data-id="element-4577">
                        Settlement summary
                      </div>
                      <div className="text-sm text-gray-600 space-y-1" data-id="element-4578">
                        <div className="flex justify-between" data-id="element-4579">
                          <span data-id="element-4580">Current outstanding</span>
                          <span className="font-medium" data-id="element-4581">
                            {formatMYR(facility.outstanding)}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-4582">
                          <span data-id="element-4583">New outstanding</span>
                          <span className="font-medium" data-id="element-4584">
                            {formatMYR(newOutstanding)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3" data-id="element-4585">
                      <button onClick={() => setPayEarlyStep('method')} className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors" data-id="element-4586">
                        Back
                      </button>
                      <button onClick={() => setPayEarlyStep('impact')} disabled={!payEarlyAmount || payEarlyAmount < 0 || payEarlyAmount > facility.outstanding} className="flex-1 py-3 bg-[var(--brand-primary)] text-white rounded-lg font-medium hover:bg-[var(--brand-primary-hover)] transition-colors disabled:opacity-50" data-id="element-4587">
                        Continue
                      </button>
                    </div>
                  </div>}

                {payEarlyStep === 'impact' && <div className="space-y-6" data-id="element-4588">
                    <div className="bg-blue-50 p-4 rounded-lg flex items-start gap-3 border border-blue-100" data-id="element-4589">
                      <InfoIcon size={18} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-4590" />
                      <div className="text-sm text-blue-800" data-id="element-4591">
                        <p className="font-medium mb-1" data-id="element-4592">Fixed-fee model</p>
                        <p data-id="element-4593">
                          Paying early reduces your outstanding balance. The
                          total repayment amount is fixed, so early repayment
                          shortens the time to settlement.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4" data-id="element-4594">
                      <div className="flex justify-between items-center py-3 border-b border-gray-100" data-id="element-4595">
                        <span className="text-gray-600" data-id="element-4596">
                          Current outstanding
                        </span>
                        <span className="font-medium text-gray-900" data-id="element-4597">
                          {formatMYR(facility.outstanding)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-gray-100" data-id="element-4598">
                        <span className="text-gray-600" data-id="element-4599">Early repayment</span>
                        <span className="font-medium text-red-600" data-id="element-4600">
                          -{formatMYR(payEarlyAmount)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-3" data-id="element-4601">
                        <span className="font-bold text-gray-900" data-id="element-4602">
                          New outstanding
                        </span>
                        <span className="font-bold text-gray-900" data-id="element-4603">
                          {formatMYR(newOutstanding)}
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-3" data-id="element-4604">
                      <button onClick={() => setPayEarlyStep('amount')} className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors" data-id="element-4605">
                        Back
                      </button>
                      <button onClick={() => setPayEarlyStep('confirm')} className="flex-1 py-3 bg-[var(--brand-primary)] text-white rounded-lg font-medium hover:bg-[var(--brand-primary-hover)] transition-colors" data-id="element-4606">
                        Continue
                      </button>
                    </div>
                  </div>}

                {payEarlyStep === 'confirm' && <div className="space-y-6 text-center py-4" data-id="element-4607">
                    <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4608">
                      <TrendingUpIcon size={28} className="text-[var(--brand-primary)]" data-id="element-4609" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900" data-id="element-4610">
                      Confirm early repayment
                    </h3>
                    <p className="text-gray-600" data-id="element-4611">
                      You're about to repay{' '}
                      <strong className="text-gray-900" data-id="element-4612">
                        {formatMYR(payEarlyAmount)}
                      </strong>{' '}
                      of facility {facility.id}.
                    </p>
                    <div className="flex gap-3 mt-8" data-id="element-4613">
                      <button onClick={() => setPayEarlyStep('impact')} className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors" data-id="element-4614">
                        Back
                      </button>
                      <button onClick={() => setPayEarlyStep('success')} className="flex-1 py-3 bg-[var(--brand-primary)] text-white rounded-lg font-medium hover:bg-[var(--brand-primary-hover)] transition-colors" data-id="element-4615">
                        Confirm payment
                      </button>
                    </div>
                  </div>}

                {payEarlyStep === 'success' && <div className="space-y-6 text-center py-8" data-id="element-4616">
                    <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4617">
                      <CheckCircle2Icon size={32} className="text-green-600" data-id="element-4618" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900" data-id="element-4619">
                      Payment scheduled
                    </h3>
                    <p className="text-gray-600" data-id="element-4620">
                      Your early repayment of{' '}
                      <strong className="text-gray-900" data-id="element-4621">
                        {formatMYR(payEarlyAmount)}
                      </strong>{' '}
                      has been scheduled.
                    </p>
                    <button onClick={handlePayEarlyClose} className="w-full py-3 bg-[var(--brand-primary)] text-white rounded-lg font-medium hover:bg-[var(--brand-primary-hover)] transition-colors" data-id="element-4622">
                      Done
                    </button>
                  </div>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}